<script>
import { mapGetters } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useTrack } from 'dashboard/composables';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import {
  resolveActionName,
  resolveTeamIds,
  resolveLabels,
  resolveAgents,
} from 'dashboard/routes/dashboard/settings/macros/macroHelper';
import MacroInfoModal from './MacroInfoModal.vue';

export default {
  name: 'ContactInfoMacro',
  components: {
    MacroInfoModal,
  },
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const { accountScopedUrl } = useAccount();

    return {
      accountScopedUrl,
    };
  },
  data() {
    return {
      executingMacros: {}, // Track which macros are executing
      previewMacros: {}, // Track which macros are showing preview
      showInfoModal: false,
      selectedMacro: null,
    };
  },
  computed: {
    ...mapGetters({
      macros: ['macros/getMacros'],
      uiFlags: 'macros/getUIFlags',
      labels: 'labels/getLabels',
      teams: 'teams/getTeams',
      agents: 'agents/getAgents',
    }),
  },
  mounted() {
    this.$store.dispatch('macros/get');
  },
  methods: {
    async executeMacro(macro) {
      try {
        this.executingMacros[macro.id] = true;
        await this.$store.dispatch('macros/execute', {
          macroId: macro.id,
          conversationIds: [this.conversationId],
        });
        useTrack(CONVERSATION_EVENTS.EXECUTED_A_MACRO);
        useAlert(this.$t('MACROS.EXECUTE.EXECUTED_SUCCESSFULLY'));
      } catch (error) {
        useAlert(this.$t('MACROS.ERROR'));
      } finally {
        this.executingMacros[macro.id] = false;
      }
    },
    toggleMacroPreview(macro) {
      const isShowing = this.previewMacros[macro.id];
      this.previewMacros[macro.id] = !isShowing;
    },
    closeMacroPreview(macro) {
      this.previewMacros[macro.id] = false;
    },
    getResolvedMacro(macro) {
      return macro.actions.map(action => {
        return {
          actionName: resolveActionName(action.action_name),
          actionValue: this.getActionValue(
            action.action_name,
            action.action_params
          ),
        };
      });
    },
    getActionValue(key, params) {
      const actionsMap = {
        assign_team: resolveTeamIds(this.teams, params),
        add_label: resolveLabels(this.labels, params),
        remove_label: resolveLabels(this.labels, params),
        assign_agent: resolveAgents(this.agents, params),
        mute_conversation: null,
        snooze_conversation: null,
        resolve_conversation: null,
        remove_assigned_team: null,
        send_webhook_event: params[0],
        send_message: params[0],
        send_email_transcript: params[0],
        add_private_note: params[0],
      };
      return actionsMap[key] || '';
    },
    openInfoModal(macro) {
      this.selectedMacro = macro;
      this.showInfoModal = true;
    },
    closeInfoModal() {
      this.showInfoModal = false;
      this.selectedMacro = null;
    },
  },
};
</script>

<template>
  <div>
    <!-- Empty State -->
    <div
      v-if="!uiFlags.isFetching && !macros.length"
      class="py-1 px-1 border-t border-n-weak"
    >
      <div class="flex items-center bg-n-solid-1 hover:bg-n-slate-3 transition-colors rounded-lg p-3">
        <div class="w-6 text-slate-500 dark:text-slate-400 text-center mr-3">
          <span class="i-lucide-zap" />
        </div>
        <div class="flex-grow">
          <div class="text-xs text-slate-500 dark:text-slate-400 mb-1">
            Macros
          </div>
          <div class="text-sm font-medium text-slate-700 dark:text-slate-200">
            Nenhum macro encontrado
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="uiFlags.isFetching" class="py-1 px-1 border-t border-n-weak">
      <div class="flex items-center bg-n-solid-1 hover:bg-n-slate-3 transition-colors rounded-lg p-3">
        <div class="w-6 text-slate-500 dark:text-slate-400 text-center mr-3">
          <span class="i-lucide-zap" />
        </div>
        <div class="flex-grow">
          <div class="text-xs text-slate-500 dark:text-slate-400 mb-1">
            Macros
          </div>
          <div class="text-sm font-medium text-slate-700 dark:text-slate-200">
            {{ $t('MACROS.LOADING') }}
          </div>
        </div>
      </div>
    </div>
    
    <!-- Macros List -->
    <div
      v-for="macro in macros"
      :key="macro.id"
      class="py-1 px-1 border-t border-n-weak relative"
    >
      <div
        class="flex items-center bg-n-solid-1 hover:bg-n-slate-3 transition-colors rounded-lg p-3 cursor-pointer group"
        @click="executeMacro(macro)"
      >
        <div class="w-6 h-6 mr-3 relative flex-shrink-0">
          <span class="i-lucide-zap absolute inset-0 m-auto opacity-100 transition-opacity duration-200 group-hover:opacity-0" />
          <span class="i-lucide-play absolute inset-0 m-auto text-n-brand opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
        </div>
        <div class="flex-grow">
          <div class="text-xs text-slate-500 dark:text-slate-400 mb-1 group-hover:text-n-brand group-hover:font-medium">
            Clique para rodar macro
          </div>
          <div class="text-sm font-medium text-slate-700 dark:text-slate-200">
            {{ macro.name }}
          </div>
        </div>
        <div class="flex items-center gap-1">
          <button
            v-tooltip.left-start="$t('MACROS.EXECUTE.PREVIEW')"
            class="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors p-1"
            @click.stop="openInfoModal(macro)"
          >
            <span class="i-lucide-info text-sm" />
          </button>
        </div>
      </div>
      
      <!-- Macro Preview -->
      <transition name="menu-slide">
        <div
          v-if="previewMacros[macro.id]"
          v-on-clickaway="() => closeMacroPreview(macro)"
          class="macro-preview absolute border border-n-weak max-h-[22.5rem] z-50 w-64 rounded-md bg-n-alpha-3 backdrop-blur-[100px] shadow-lg bottom-full right-0 mb-2 overflow-y-auto p-4 text-left rtl:text-right"
        >
          <h6 class="mb-4 text-sm text-n-slate-12">
            {{ macro.name }}
          </h6>
          <div
            v-for="(action, i) in getResolvedMacro(macro)"
            :key="i"
            class="relative pl-4 macro-block"
          >
            <div
              v-if="i !== macro.actions.length - 1"
              class="top-[0.390625rem] absolute -bottom-1 left-0 w-px bg-slate-75 dark:bg-slate-600"
            />
            <div
              class="absolute -left-[0.21875rem] top-[0.2734375rem] w-2 h-2 rounded-full bg-n-solid-1 border-2 border-solid border-n-weak dark:border-slate-600"
            />
            <p class="mb-1 text-xs text-n-slate-11">
              {{ action.actionName }}
            </p>
            <p class="text-n-slate-12 text-sm">{{ action.actionValue }}</p>
          </div>
        </div>
      </transition>
    </div>
    
    <!-- Modal -->
    <MacroInfoModal
      :show="showInfoModal"
      :macro="selectedMacro"
      :teams="teams"
      :agents="agents"
      :labels="labels"
      @close="closeInfoModal"
    />
  </div>
</template>

<style scoped lang="scss">
.macro-preview {
  .macro-block {
    &:not(:last-child) {
      padding-bottom: 0.5rem;
    }
  }
}



.menu-slide-enter-active,
.menu-slide-leave-active {
  transition: all 0.3s ease;
}

.menu-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.menu-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>